import {Box, styled} from "@mui/material";
import React, {useEffect, useState} from "react";
import {factors} from "../../config/factors";
import AIDoingThings from "../../assets/AIDoingThings.png";
import {getRandomElsFromArray} from "../../helpers/getRandomElsFromArray";
import {FactorInterface} from "../../interfaces/FactorInterface";
import AIMenuAnalytics from "./AIMenuAnalytics";

interface AIMenuInterface {
    articlesSettings: number[];
}

const AIMenuContainer = styled(Box)({
    height: "300px",
    minHeight: "300px",
    width: "300px",
    background: "#2F2F2F",
    transition: "all .3s ease",
    "&.results":{
        width: "500px",
        height: "auto",
    },
    "@media(max-width: 768px)":{
        width: "calc(100vw - 25px) !important",
    }
});

const AIMenuAnimationContainer = styled(Box)({
    height: "300px",
    width: "300px",
    position: "relative",
    background: "#2F2F2F",
    overflow: "hidden",
    margin: "auto",
});

const AIMenuImg = styled(Box)({
    background: `url(${AIDoingThings})`,
    backgroundSize: "contain",
    height: "300px",
    width: "300px",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 3
});

const AIMenuAnimation1 = styled(Box)({
    background: "#8B0000",
    height: "3px",
    width: "200px",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 2,
    boxShadow: "0 0 70px 50px #8B0000",
    animation: "outer-animation1 6s linear infinite",
    "@keyframes outer-animation1": {
        "0%": {
            transform: "translate(0, 0) rotate(0deg)",
        },
        "25%": {
            transform: "translate(200px, 100px) rotate(560deg)"
        },
        "50%": {
            transform: "translate(100px, 300px) rotate(1360deg)"
        },
        "75%": {
            transform: "translate(-50px, 100px) rotate(1800deg)"
        },
        "100%": {
            transform: "translate(0, 0) rotate(2000deg)"
        }
    },
});

const AIMenuAnimation2 = styled(Box)({
    background: "#8B0000",
    height: "3px",
    width: "200px",
    position: "absolute",
    bottom: 0,
    right: 0,
    zIndex: 2,
    boxShadow: "0 0 70px 50px #8B0000",
    animation: "outer-animation2 6s .1s linear infinite",
    "@keyframes outer-animation2": {
        "0%": {
            transform: "translate(0, 0) rotate(0deg)",
        },
        "25%": {
            transform: "translate(-200px, -100px) rotate(-560deg)"
        },
        "50%": {
            transform: "translate(-100px, -300px) rotate(-1360deg)"
        },
        "75%": {
            transform: "translate(50px, -100px) rotate(-1800deg)"
        },
        "100%": {
            transform: "translate(0, 0) rotate(-2300deg)"
        }
    },
});

const AIMenuAnimationRed = styled(Box)({
    background: "#B30000",
    opacity: 0.8,
    width: 0,
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 2,
    animation: "create-animation1 .6s ease-in-out",
    animationFillMode: "forwards",
});

const AIMenuAnimationYellow = styled(Box)({
    background: "#F1C40F",
    opacity: 0.8,
    height: "100%",
    width: 0,
    position: "absolute",
    top: 0,
    zIndex: 2,
    animation: "create-animation2 .6s .6s ease-in",
    animationFillMode: "forwards",
});

const AIMenuAnimationGreen = styled(Box)({
    background: "#5bbf83",
    opacity: 0.8,
    height: "100%",
    width: 0,
    position: "absolute",
    top: 0,
    zIndex: 2,
    animation: "create-animation3 .5s 1.3s ease-out",
    animationFillMode: "forwards",
});



const AIMenu = ({articlesSettings}:AIMenuInterface):JSX.Element => {
    
    const [loading, setLoading] = useState<boolean>(true);
    const [articles, setArticles] = useState<FactorInterface[][]>([]);
    const [percents, setPercents] = useState<number[]>([]);
    
    useEffect(() => {
        getArticles();
        setTimeout(() => {
            setLoading(false);
        },1000);
    },[]);
    
    const getArticles = ():void => {
        const keysArr = ["drop", "stable", "grow"];
        const articlesCalc = articlesSettings.map((opt, index) => getRandomElsFromArray(factors.filter((el) => el.possibilities.key === keysArr[index]), opt));
        const summ = articlesCalc.flat(2).map((opt) => opt.possibilities.value).reduce((a, b) => a+b);
        const percentsCalc = articlesCalc.map((opt: FactorInterface[]) => Math.round(opt.map((el: FactorInterface) => el.possibilities.value).reduce((a: number, b: number) => a+b)/summ*100));
        setPercents(percentsCalc);
        setArticles(articlesCalc);
    };
    
    const returnAnimatedElements = ():React.ReactNode => {
        if(loading){
            return (
                <>
                    <AIMenuAnimation1/>
                    <AIMenuAnimation2/>
                </>
            );
        }
        return (
            <>
                <AIMenuAnimationRed sx={{
                    "@keyframes create-animation1": {
                        "to": {
                            width: percents[0] + "%",
                        },
                    },
                }}/>
                <AIMenuAnimationYellow sx={{
                    "@keyframes create-animation2": {
                        "to": {
                            width: percents[1] + "%",
                        },
                    },
                    left: percents[0] + "%",
                }}/>
                <AIMenuAnimationGreen sx={{
                    
                    "@keyframes create-animation3": {
                        "to": {
                            width: percents[2] + "%",
                        },
                    },
                    left: (percents[0] + percents[1]) + "%",
                }}/>
            </>
        );
    };
    
    return (
        <AIMenuContainer className={loading ? "" : "results"}>
            <AIMenuAnimationContainer>
                <AIMenuImg/>
                {returnAnimatedElements()}
            </AIMenuAnimationContainer>
            {!loading && <AIMenuAnalytics
                percents={percents}
                articles={articles}
            />}
        </AIMenuContainer>
    );
};

export default AIMenu;
